import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, money, formatErr } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await http.get(`/admin/products${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await http.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Products</h1>
          <p className="text-white/45 text-sm mt-1">{items.length} total · click a row to edit</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name…"
              className="bls-admin-input pl-9"
              data-testid="admin-products-search"
            />
          </div>
          <Link to="/admin/products/new" className="bls-admin-btn" data-testid={TID.adminProductNew}>
            <Plus className="w-4 h-4" /> New product
          </Link>
        </div>
      </div>

      <div className="mt-8 bls-admin-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th className="bls-admin-th">Product</th>
              <th className="bls-admin-th">Status</th>
              <th className="bls-admin-th">Stock</th>
              <th className="bls-admin-th">Price</th>
              <th className="bls-admin-th text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="bls-admin-td text-center text-white/45">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="bls-admin-td text-center text-white/45">No products yet — add your first one</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="bls-admin-tr" data-testid={`${TID.adminProductRow}-${p.slug}`}>
                <td className="bls-admin-td">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#16161f] overflow-hidden flex-shrink-0">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <Link to={`/admin/products/${p.id}`} className="font-medium hover:text-[#7f77dd]">{p.name}</Link>
                      <p className="text-xs text-white/40 font-mono">{p.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="bls-admin-td">
                  <span className={`bls-status ${p.is_active ? "bls-status-paid" : "bls-status-failed"}`}>
                    {p.is_active ? "Active" : "Hidden"}
                  </span>
                  {p.is_featured && <span className="ml-2 bls-status bls-status-processing">Featured</span>}
                </td>
                <td className="bls-admin-td">{p.stock}</td>
                <td className="bls-admin-td">{money(p.price)}</td>
                <td className="bls-admin-td text-right">
                  <Link to={`/admin/products/${p.id}`} className="bls-admin-btn-ghost mr-2">
                    <Pencil className="w-3 h-3" /> Edit
                  </Link>
                  <button onClick={() => remove(p.id)} className="bls-admin-btn-ghost" data-testid={`${TID.adminProductDelete}-${p.slug}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
